/**
 * Authoritative customer-facing deal value for Portal cards, pipeline, and BOM.
 *
 * Rule: sum(authoritative line.lineTotal) for customer-visible lines only.
 * A line counts only when its system and room/location are customer-visible
 * (and the line bucket is a customer commercial bucket).
 *
 * Visibility source: parsedMetadataJson.dtoolsDisplaySettings
 *   - hiddenOptionKeys (+ customerExposeInternalOptionKeys / withInternalSystemsHiddenByDefault)
 *   - hiddenLocationKeys (stable locationId keys; hidden rooms contribute $0)
 *
 * Does not use header.totalAmount, reconciliation.headerTotal, or qty×unitPrice.
 * Does not mutate procurement / invoice totalAmount.
 */
import {
  filterVisibleDtoolsSystems,
  isCustomerFacingDtoolsSystem,
  withInternalSystemsHiddenByDefault,
} from "./dtoolsOptionVisibility";
import type { DtoolsDisplaySettings } from "./dtoolsDisplaySettings";
import { resolveDtoolsSystemContentKey } from "./dtoolsProposalContent";
import { sumCustomerLineTotalsInHierarchySystem } from "./dtoolsReviewNavigation";
import { isServiceLine } from "./dtoolsReviewNavigation";
import { isDtoolsInternalImportSystem } from "./dtoolsInternalSystems";
import type { DtoolsReviewSystem } from "./dtoolsStagingTransform";
import { isDtoolsJsonProposalSourceValue } from "./proposalSources";
import {
  resolveDtoolsCustomerTabGate,
  resolveSystemClassification,
  type DtoolsSystemClassificationsMap,
  type DtoolsTabModelVersion,
} from "./dtoolsSystemClassification";
import {
  hasNegotiatedAgreedPrice,
  resolveProposalCustomerPricingDisplay,
  sumProposalItemLineTotals,
  type RetailPricingJson,
} from "./proposalCustomerPricing";
import {
  buildDtoolsLiveProposalViewModel,
  type BuildDtoolsLiveProposalViewOptions,
  type ProposalDbItemForDtoolsView,
  type ProposalHeaderForDtoolsView,
} from "./dtoolsProposalLiveView";

const CACHE_KEY = "customerFacingDealTotal";

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

function moneyNumber(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Σ line.lineTotal for customer-visible systems and rooms (and accessories under those),
 * PLUS every priced customer line parked in a D-Tools internal placeholder system
 * ("Unassigned system", "Unknown system", blank) — see collectInternalSystemCustomerSections.
 * Priced lines in Unassigned are never silently dropped from the customer Grand Total.
 */
export function sumVisibleDtoolsSystemsLineTotals(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
  classificationContext?: DtoolsTotalsClassificationContext | null,
): number {
  return computeVisibleDtoolsSystemsOptionTotals(systems, displaySettings, classificationContext).total;
}

/**
 * V2 classification context (tab model + per-system independent/alternative classification).
 * When it resolves to the V2 tab model, alternativeOptions=true means "choose one option per
 * alternative GROUP" — independent systems are always added. Without it (V1 / unclassified) the
 * legacy rule applies: every visible system is one option and only the highest counts.
 */
export type DtoolsTotalsClassificationContext = {
  tabModelVersion?: DtoolsTabModelVersion | null;
  systemClassifications?: DtoolsSystemClassificationsMap | null;
  parsedMetadataJson?: unknown;
};

export type DtoolsTotalsResolution =
  | "sum_all_visible"
  | "max_visible_system"
  | "classified_alternative_groups"
  | "no_visible_systems";

/**
 * Customer-visible proposal total with the option range.
 * - total / upToTotal: most expensive option per alternative group (single-number authority used
 *   for stored totalAmount, deal value, Insights — existing highest-only convention)
 * - fromTotal: cheapest option per alternative group ("From S$x to S$y depending on options chosen")
 */
export type DtoolsVisibleOptionTotals = {
  total: number;
  fromTotal: number;
  upToTotal: number;
  /** True when fromTotal < upToTotal (real choose-one options with different prices). */
  hasOptionRange: boolean;
  /** Visible option value NOT counted in `total` (non-highest options of each group). */
  alternativeOptionsNotIncludedTotal: number;
  /** Alternative groups with >= 2 visible options. */
  alternativeGroupCount: number;
  includedSystemKeys: string[];
  offeredOptionSystemKeys: string[];
  resolution: DtoolsTotalsResolution;
};

/** Same group key rule as buildAlternativeGroups (dtoolsV2CustomerProposalView) so totals match the customer link. */
export function dtoolsAlternativeGroupKey(groupName: string | null | undefined, systemName: string): string {
  return (String(groupName ?? "").trim() || String(systemName ?? "").trim() || "Alternatives").toLowerCase();
}

/** Resolved classifications when the proposal uses the V2 tab model; null → legacy V1 rule. */
export function resolveTotalsClassifications(
  systems: Array<{ systemId?: string | null; name: string }>,
  context?: DtoolsTotalsClassificationContext | null,
): DtoolsSystemClassificationsMap | null {
  if (!context) return null;
  const gate = resolveDtoolsCustomerTabGate({
    systems,
    tabModelVersion: context.tabModelVersion ?? undefined,
    parsedMetadataJson: context.parsedMetadataJson,
    systemClassifications:
      context.systemClassifications && Object.keys(context.systemClassifications).length > 0
        ? context.systemClassifications
        : undefined,
  });
  return gate.useV2TabModel ? gate.systemClassifications : null;
}

type VisibleOptionEntry = {
  key: string;
  total: number;
  optionIndex: number;
  /** null → independent (always included). */
  groupKey: string | null;
};

function pickMax(entries: VisibleOptionEntry[]): VisibleOptionEntry {
  const maxTotal = Math.max(...entries.map((e) => e.total));
  return entries
    .filter((e) => Math.abs(e.total - maxTotal) < 0.005)
    .sort((a, b) => a.optionIndex - b.optionIndex || a.key.localeCompare(b.key))[0]!;
}

function computeOptionTotalsFromEntries(
  visible: VisibleOptionEntry[],
  alternativeOptions: boolean,
  classified: boolean,
): Omit<DtoolsVisibleOptionTotals, "total" | "fromTotal" | "upToTotal" | "hasOptionRange"> & {
  upTo: number;
  from: number;
} {
  if (!visible.length) {
    return {
      upTo: 0,
      from: 0,
      alternativeOptionsNotIncludedTotal: 0,
      alternativeGroupCount: 0,
      includedSystemKeys: [],
      offeredOptionSystemKeys: [],
      resolution: "no_visible_systems",
    };
  }
  const sumAll = visible.reduce((sum, e) => sum + e.total, 0);
  if (!alternativeOptions) {
    return {
      upTo: sumAll,
      from: sumAll,
      alternativeOptionsNotIncludedTotal: 0,
      alternativeGroupCount: 0,
      includedSystemKeys: visible.map((e) => e.key),
      offeredOptionSystemKeys: [],
      resolution: "sum_all_visible",
    };
  }
  if (!classified) {
    // Legacy V1: every visible system is one option of a single choose-one set.
    const max = pickMax(visible);
    const min = Math.min(...visible.map((e) => e.total));
    return {
      upTo: max.total,
      from: min,
      alternativeOptionsNotIncludedTotal: sumAll - max.total,
      alternativeGroupCount: visible.length >= 2 ? 1 : 0,
      includedSystemKeys: [max.key],
      offeredOptionSystemKeys: visible.map((e) => e.key).filter((k) => k !== max.key),
      resolution: "max_visible_system",
    };
  }
  // V2: independent systems always count; each alternative group contributes one option.
  const independent = visible.filter((e) => e.groupKey == null);
  const groupOrder: string[] = [];
  const groups = new Map<string, VisibleOptionEntry[]>();
  for (const e of visible) {
    if (e.groupKey == null) continue;
    if (!groups.has(e.groupKey)) {
      groups.set(e.groupKey, []);
      groupOrder.push(e.groupKey);
    }
    groups.get(e.groupKey)!.push(e);
  }
  const independentTotal = independent.reduce((sum, e) => sum + e.total, 0);
  let upTo = independentTotal;
  let from = independentTotal;
  let notIncluded = 0;
  let realGroups = 0;
  const included = independent.map((e) => e.key);
  const offered: string[] = [];
  for (const key of groupOrder) {
    const options = groups.get(key)!;
    const max = pickMax(options);
    upTo += max.total;
    from += Math.min(...options.map((e) => e.total));
    notIncluded += options.reduce((sum, e) => sum + e.total, 0) - max.total;
    if (options.length >= 2) realGroups += 1;
    included.push(max.key);
    for (const e of options) if (e.key !== max.key) offered.push(e.key);
  }
  return {
    upTo,
    from,
    alternativeOptionsNotIncludedTotal: notIncluded,
    alternativeGroupCount: realGroups,
    includedSystemKeys: included,
    offeredOptionSystemKeys: offered,
    resolution: "classified_alternative_groups",
  };
}

function finalizeOptionTotals(
  core: ReturnType<typeof computeOptionTotalsFromEntries>,
  addBack: number,
): DtoolsVisibleOptionTotals {
  const upToTotal = roundMoney(core.upTo + addBack);
  const fromTotal = roundMoney(core.from + addBack);
  return {
    total: upToTotal,
    upToTotal,
    fromTotal,
    hasOptionRange: upToTotal - fromTotal > 0.005,
    alternativeOptionsNotIncludedTotal: roundMoney(Math.max(0, core.alternativeOptionsNotIncludedTotal)),
    alternativeGroupCount: core.alternativeGroupCount,
    includedSystemKeys: core.includedSystemKeys,
    offeredOptionSystemKeys: core.offeredOptionSystemKeys,
    resolution: core.resolution,
  };
}

/**
 * Σ line.lineTotal for customer-visible systems and rooms (and accessories under those),
 * PLUS every priced customer line parked in a D-Tools internal placeholder system — see
 * collectInternalSystemCustomerSections. With alternativeOptions=true, V2-classified proposals add
 * every independent system plus one option per alternative group (highest for `total`, cheapest for
 * `fromTotal`); V1/unclassified proposals keep the legacy highest-visible-system rule.
 */
export function computeVisibleDtoolsSystemsOptionTotals(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
  classificationContext?: DtoolsTotalsClassificationContext | null,
): DtoolsVisibleOptionTotals {
  const classifications = resolveTotalsClassifications(systems, classificationContext);
  const visible: VisibleOptionEntry[] = filterVisibleDtoolsSystems(systems, displaySettings, {
    audience: "customer",
  }).map(({ system, optionIndex }) => {
    const entry = classifications
      ? resolveSystemClassification(classifications, system.systemId, system.name)
      : null;
    return {
      key: resolveDtoolsSystemContentKey(system),
      total: sumCustomerLineTotalsInHierarchySystem(system),
      optionIndex,
      groupKey:
        entry?.classification === "alternative" ? dtoolsAlternativeGroupKey(entry.groupName, system.name) : null,
    };
  });
  const internalSectionsTotal = sumInternalSystemCustomerSections(systems, displaySettings);
  return finalizeOptionTotals(
    computeOptionTotalsFromEntries(visible, displaySettings?.alternativeOptions === true, classifications != null),
    internalSectionsTotal,
  );
}

type DtoolsReviewBlock = DtoolsReviewSystem["systemLevelPrimaries"][number];

/** Buckets that count toward the customer total (same set as sumCustomerLineTotalsInHierarchySystem). */
export const DTOOLS_CUSTOMER_TOTAL_BUCKETS: ReadonlySet<string> = new Set([
  "commercial",
  "discount",
  "service",
  "cable",
]);

/** Customer-billable value of one line: customer bucket, not VO-removed, lineTotal > 0. */
export function dtoolsLineCustomerValue(line: DtoolsReviewBlock["line"]): number {
  if (!DTOOLS_CUSTOMER_TOTAL_BUCKETS.has(line.bucket)) return 0;
  if (line.variationOrderAnnotation?.kind === "removed") return 0;
  return Math.max(0, Number(line.lineTotal) || 0);
}

function blockCustomerValue(block: DtoolsReviewBlock): number {
  return (
    dtoolsLineCustomerValue(block.line) +
    block.accessories.reduce((sum, accessory) => sum + dtoolsLineCustomerValue(accessory), 0)
  );
}

/**
 * Internal placeholder systems whose lines must be added back on top of the visible systems.
 * An internal system that staff explicitly exposed as its own customer section is already counted
 * through the visible systems, so it is not added back (no double count). Hidden-by-default AND
 * explicitly staff-hidden internal systems are always added back — hiding "Unassigned" never
 * removes priced lines from the Grand Total.
 */
function internalSystemsForAddBack(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
): DtoolsReviewSystem[] {
  return systems.filter(
    (system) =>
      isDtoolsInternalImportSystem(system) &&
      !isCustomerFacingDtoolsSystem(system, displaySettings, "customer"),
  );
}

function filterInternalSystemBlocks(
  system: DtoolsReviewSystem,
  keep: (block: DtoolsReviewBlock) => boolean,
): Pick<DtoolsReviewSystem, "rooms" | "systemLevelPrimaries"> {
  const systemLevelPrimaries = system.systemLevelPrimaries.filter(keep);
  const rooms = system.rooms
    .map((room) => ({ ...room, primaries: room.primaries.filter(keep) }))
    .filter((room) => room.primaries.length > 0);
  return { rooms, systemLevelPrimaries };
}

/** Project-wide service lines remain billable even when D-Tools left them in its internal Unassigned system. */
export function collectGlobalBillableServiceSystems(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
): DtoolsReviewSystem[] {
  const out: DtoolsReviewSystem[] = [];
  for (const system of internalSystemsForAddBack(systems, displaySettings)) {
    const keep = (block: DtoolsReviewBlock) =>
      isServiceLine(block.line) && block.line.variationOrderAnnotation?.kind !== "removed";
    const { systemLevelPrimaries, rooms } = filterInternalSystemBlocks(system, keep);
    if (systemLevelPrimaries.length || rooms.length) {
      out.push({ ...system, name: "Services", systemLevelPrimaries, rooms });
    }
  }
  return out;
}

export const DTOOLS_UNASSIGNED_OTHER_ITEMS_SYSTEM_ID = "clarity-unassigned-other-items";
export const DTOOLS_UNASSIGNED_OTHER_ITEMS_LABEL = "Other items";

/**
 * Every other priced customer line (products, cables, accessories, child lines) parked in an internal
 * placeholder system, merged into one customer-facing "Other items" section. Zero-priced /
 * internal-only blocks stay hidden.
 */
export function collectUnassignedOtherBillableSystems(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
): DtoolsReviewSystem[] {
  const rooms: DtoolsReviewSystem["rooms"] = [];
  const systemLevelPrimaries: DtoolsReviewBlock[] = [];
  for (const system of internalSystemsForAddBack(systems, displaySettings)) {
    const keep = (block: DtoolsReviewBlock) =>
      !isServiceLine(block.line) &&
      block.line.variationOrderAnnotation?.kind !== "removed" &&
      blockCustomerValue(block) > 0.004;
    const kept = filterInternalSystemBlocks(system, keep);
    rooms.push(...kept.rooms);
    systemLevelPrimaries.push(...kept.systemLevelPrimaries);
  }
  if (!rooms.length && !systemLevelPrimaries.length) return [];
  return [
    {
      systemId: DTOOLS_UNASSIGNED_OTHER_ITEMS_SYSTEM_ID,
      name: DTOOLS_UNASSIGNED_OTHER_ITEMS_LABEL,
      rooms,
      systemLevelPrimaries,
    },
  ];
}

/**
 * Single source of truth for priced lines in internal placeholder systems: "Services" sections
 * followed by one "Other items" section. Feeds the headline / deal card / Revise total, the customer
 * System Summary and the quotation PDF so all of them agree.
 */
export function collectInternalSystemCustomerSections(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
): DtoolsReviewSystem[] {
  return [
    ...collectGlobalBillableServiceSystems(systems, displaySettings),
    ...collectUnassignedOtherBillableSystems(systems, displaySettings),
  ];
}

export function sumInternalSystemCustomerSections(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
): number {
  return roundMoney(
    collectInternalSystemCustomerSections(systems, displaySettings).reduce(
      (sum, system) => sum + sumCustomerLineTotalsInHierarchySystem(system),
      0,
    ),
  );
}

/** Services-only portion of the internal add-back (kept for callers that label services separately). */
export function sumGlobalBillableServicesInInternalSystems(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
): number {
  return roundMoney(
    collectGlobalBillableServiceSystems(systems, displaySettings).reduce(
      (sum, system) => sum + sumCustomerLineTotalsInHierarchySystem(system),
      0,
    ),
  );
}

/**
 * Partition D-Tools systems using the SAME commercial authority as deal totals.
 *
 * - Visibility: customer-facing filter (+ default-hide internal import systems)
 * - alternativeOptions=true → included = max-valued visible system(s); others visible = offered options
 * - alternativeOptions=false/absent → all customer-visible systems are included (additive)
 * - Hidden systems are excluded from both included and offered analyses
 *
 * Tie-break when multiple visible systems share the max total: lowest optionIndex
 * (filterVisibleDtoolsSystems order), then content-key localeCompare.
 */
export type CustomerFacingSystemPartition = {
  alternativeOptions: boolean;
  includedSystemKeys: string[];
  offeredOptionSystemKeys: string[];
  hiddenSystemKeys: string[];
  /** Deal-total authority (highest option per alternative group) — matches deal-total math. */
  authoritativeVisibleTotal: number;
  /** Cheapest option per alternative group (equals authoritativeVisibleTotal when no range). */
  fromVisibleTotal: number;
  resolution: DtoolsTotalsResolution;
};

export type CustomerFacingSystemAggregate = {
  systemId?: string | null;
  /** Prefer `name`; `systemName` accepted for Insights line metadata. */
  name?: string | null;
  systemName?: string | null;
  /** Σ customer commercial buckets for this system (commercial|discount|service|cable). */
  customerLineTotal: number;
};

function systemNameFromAggregate(s: CustomerFacingSystemAggregate): string {
  return String(s.name ?? s.systemName ?? "").trim() || "Unassigned system";
}

/**
 * Lightweight partition for Insights (and similar) when only per-system aggregates exist.
 * Mirrors sumVisibleDtoolsSystemsLineTotals selection rules without rebuilding hierarchy
 * (V2 classification-aware when a classification context is passed).
 */
export function partitionCustomerFacingSystemsByDealAuthority(
  systems: CustomerFacingSystemAggregate[],
  displaySettings?: DtoolsDisplaySettings,
  classificationContext?: DtoolsTotalsClassificationContext | null,
): CustomerFacingSystemPartition {
  const shaped = systems.map((s, optionIndex) => ({
    systemId: s.systemId ?? null,
    name: systemNameFromAggregate(s),
    customerLineTotal: Math.max(0, Number(s.customerLineTotal) || 0),
    optionIndex,
  }));

  const settings = withInternalSystemsHiddenByDefault(shaped, displaySettings ?? {});
  const classifications = resolveTotalsClassifications(shaped, classificationContext);
  const hiddenSystemKeys: string[] = [];
  const visible: VisibleOptionEntry[] = [];
  for (const s of shaped) {
    const key = resolveDtoolsSystemContentKey(s);
    if (!isCustomerFacingDtoolsSystem(s, settings, "customer")) {
      hiddenSystemKeys.push(key);
      continue;
    }
    const entry = classifications ? resolveSystemClassification(classifications, s.systemId, s.name) : null;
    visible.push({
      key,
      total: s.customerLineTotal,
      optionIndex: s.optionIndex,
      groupKey: entry?.classification === "alternative" ? dtoolsAlternativeGroupKey(entry.groupName, s.name) : null,
    });
  }

  const totals = finalizeOptionTotals(
    computeOptionTotalsFromEntries(visible, settings.alternativeOptions === true, classifications != null),
    0,
  );
  return {
    alternativeOptions: settings.alternativeOptions === true,
    includedSystemKeys: totals.includedSystemKeys,
    offeredOptionSystemKeys: totals.offeredOptionSystemKeys,
    hiddenSystemKeys,
    authoritativeVisibleTotal: totals.total,
    fromVisibleTotal: totals.fromTotal,
    resolution: totals.resolution,
  };
}

/** Full hierarchy form — totals from sumCustomerLineTotalsInHierarchySystem. */
export function partitionCustomerFacingReviewSystemsByDealAuthority(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
  classificationContext?: DtoolsTotalsClassificationContext | null,
): CustomerFacingSystemPartition {
  const aggregates: CustomerFacingSystemAggregate[] = systems.map((system) => ({
    systemId: system.systemId,
    name: system.name,
    customerLineTotal: sumCustomerLineTotalsInHierarchySystem(system),
  }));
  return partitionCustomerFacingSystemsByDealAuthority(aggregates, displaySettings, classificationContext);
}

export function computeDtoolsCustomerFacingDealTotalFromLiveView(
  header: ProposalHeaderForDtoolsView,
  items: ProposalDbItemForDtoolsView[],
  options?: BuildDtoolsLiveProposalViewOptions,
): number | null {
  const viewModel = buildDtoolsLiveProposalViewModel(header, items, options);
  if (!viewModel) return null;
  return sumVisibleDtoolsSystemsLineTotals(
    viewModel.systems,
    viewModel.displaySettings,
    liveViewTotalsClassificationContext(viewModel),
  );
}

/**
 * True when a V2 proposal with "multiple options" on has at least one real alternative group
 * (2+ options to choose from). For these proposals the customer link and PDF omit the grand
 * total, and Save never rewrites stored totals or blocks a final agreed price on the option range.
 */
export function hasDtoolsAlternativeOptionGroups(
  totals: Pick<DtoolsVisibleOptionTotals, "alternativeGroupCount" | "resolution"> | null | undefined,
): boolean {
  return (
    totals != null && totals.resolution === "classified_alternative_groups" && totals.alternativeGroupCount > 0
  );
}

/** Full option totals (From / Up to / groups) for a stored proposal, via the customer live view. */
export function computeDtoolsLiveViewOptionTotals(
  header: ProposalHeaderForDtoolsView,
  items: ProposalDbItemForDtoolsView[],
  options?: BuildDtoolsLiveProposalViewOptions,
): DtoolsVisibleOptionTotals | null {
  const viewModel = buildDtoolsLiveProposalViewModel(header, items, options);
  if (!viewModel) return null;
  return computeVisibleDtoolsSystemsOptionTotals(
    viewModel.systems,
    viewModel.displaySettings,
    liveViewTotalsClassificationContext(viewModel),
  );
}

/** Classification context carried by the live view model (same tab gate as the customer link). */
export function liveViewTotalsClassificationContext(viewModel: {
  tabModelVersion?: DtoolsTabModelVersion | null;
  systemClassifications?: DtoolsSystemClassificationsMap | null;
}): DtoolsTotalsClassificationContext {
  return {
    tabModelVersion: viewModel.tabModelVersion ?? null,
    systemClassifications: viewModel.systemClassifications ?? null,
  };
}

export function readCachedCustomerFacingDealTotal(parsedMetadataJson: unknown): number | null {
  if (!parsedMetadataJson || typeof parsedMetadataJson !== "object" || Array.isArray(parsedMetadataJson)) {
    return null;
  }
  const raw = (parsedMetadataJson as Record<string, unknown>)[CACHE_KEY];
  if (raw == null) return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) && n >= 0 ? roundMoney(n) : null;
}

export function withCachedCustomerFacingDealTotal(
  parsedMetadataJson: unknown,
  total: number,
): Record<string, unknown> {
  const base =
    parsedMetadataJson && typeof parsedMetadataJson === "object" && !Array.isArray(parsedMetadataJson)
      ? { ...(parsedMetadataJson as Record<string, unknown>) }
      : {};
  base[CACHE_KEY] = roundMoney(total);
  return base;
}

/**
 * Living commercial contract total after approved VOs.
 * Prefer composed/Payment-Details revisedContractValue; else header.totalAmount when VO scope exists.
 * Do not use visible equipment line sums — those omit negotiated discounts and can inflate past the living head.
 */
export function resolveLivingCommercialContractTotal(input: {
  /** True when at least one approved VO is in scope for this proposal. */
  hasApprovedVariationOrders: boolean;
  headerTotalAmount?: unknown;
  revisedContractValue?: number | null;
}): number | null {
  if (!input.hasApprovedVariationOrders) return null;
  if (input.revisedContractValue != null && Number.isFinite(Number(input.revisedContractValue))) {
    return roundMoney(Math.max(0, Number(input.revisedContractValue)));
  }
  if (input.headerTotalAmount != null && Number.isFinite(Number(input.headerTotalAmount))) {
    return roundMoney(Math.max(0, Number(input.headerTotalAmount)));
  }
  return null;
}

/**
 * Portal deal value used by proposal cards, pipeline stage totals, and deal overview.
 * Prefer an explicit living commercial contract total (approved VO head) when provided.
 * Negotiated Final Agreed Price uses the same payable authority as the customer proposal.
 * Otherwise prefer live visible sum → cached → header.totalAmount (non-D-Tools / legacy fallback).
 */
export function resolvePortalCustomerFacingDealTotal(input: {
  totalAmount?: unknown;
  parsedMetadataJson?: unknown;
  proposalSource?: string | null;
  liveVisibleTotal?: number | null;
  retailPricingJson?: RetailPricingJson | null;
  invoiceGstEnabled?: boolean | null;
  lineItemTotalSum?: number | null;
  /** Living Commercial Head contract value after approved VOs (authoritative when set). */
  commercialContractTotal?: number | null;
}): number {
  if (input.commercialContractTotal != null && Number.isFinite(Number(input.commercialContractTotal))) {
    return roundMoney(Math.max(0, Number(input.commercialContractTotal)));
  }

  if (
    hasNegotiatedAgreedPrice(input.retailPricingJson) ||
    (input.liveVisibleTotal != null && Number.isFinite(input.liveVisibleTotal))
  ) {
    return resolveProposalCustomerPricingDisplay({
      totalAmount: input.totalAmount,
      retailPricingJson: input.retailPricingJson,
      invoiceGstEnabled: input.invoiceGstEnabled,
      computedProposalTotal: input.liveVisibleTotal,
      lineItemTotalSum: input.lineItemTotalSum,
    }).finalAmountPayable;
  }

  const cached = readCachedCustomerFacingDealTotal(input.parsedMetadataJson);
  if (cached != null) return cached;
  return roundMoney(Math.max(0, moneyNumber(input.totalAmount)));
}

/** Staff-facing proposal value — same resolver as cards, pipeline, and deal workspace. */
export function resolveStaffFacingProposalDealValue(input: {
  totalAmount?: unknown;
  parsedMetadataJson?: unknown;
  proposalSource?: string | null;
  liveVisibleTotal?: number | null;
  retailPricingJson?: RetailPricingJson | null;
  invoiceGstEnabled?: boolean | null;
  items?: Array<{ lineTotal?: unknown }>;
  commercialContractTotal?: number | null;
}): number {
  return resolvePortalCustomerFacingDealTotal({
    totalAmount: input.totalAmount,
    parsedMetadataJson: input.parsedMetadataJson,
    proposalSource: input.proposalSource,
    liveVisibleTotal: input.liveVisibleTotal,
    retailPricingJson: input.retailPricingJson,
    invoiceGstEnabled: input.invoiceGstEnabled,
    lineItemTotalSum:
      input.items && input.items.length > 0 ? sumProposalItemLineTotals(input.items) : null,
    commercialContractTotal: input.commercialContractTotal,
  });
}

export function shouldComputeDtoolsCustomerFacingDealTotal(proposalSource?: string | null): boolean {
  return isDtoolsJsonProposalSourceValue(proposalSource);
}
